// The directors' dashboard (15 §15.1): the rules behind every number on `dashboards/{period}__{scope}`.
// Pure functions over plain facts. The job in `functions/src/insights` reads the documents, calls
// these, and validates the result against the schema. Nothing here knows Firestore.
//
// Conventions: money is integer minor units; days have two decimals; percentages one; a metric with
// nothing to measure is null, never 0.
import { addDays, timezoneOffsetMinutes } from './business-date';
import { getAccount } from './accounting';
import type { BoxStatus } from './box-types';
import { LEG_KEYS, type LegDays, type LegKey } from './legs';
import { round2 } from './operations-snapshot';
import { daysBetweenDates } from './season-board';
import { meanOf, msToDays, percentOf, round1, summariseDays } from './stats';

const DAY_MS = 86_400_000;
const HOUR_MS = 3_600_000;

// -------------------------------------------------------------------------- periods

export const DASHBOARD_PERIODS = ['day', 'week', 'month'] as const;
export type DashboardPeriod = (typeof DASHBOARD_PERIODS)[number];

/** Business dates, both inclusive. */
export interface DateRange {
  from: string;
  to: string;
}

function lastDayOfMonth(first: string): string {
  const [y, m] = first.split('-').map(Number) as [number, number];
  return new Date(Date.UTC(y, m, 0)).toISOString().slice(0, 10);
}

/** The day, ISO week (Monday to Sunday) or month that contains `today`. */
export function periodRange(period: DashboardPeriod, today: string): DateRange {
  if (period === 'day') return { from: today, to: today };
  if (period === 'week') {
    const weekday = new Date(`${today}T00:00:00Z`).getUTCDay();
    const monday = addDays(today, -((weekday + 6) % 7));
    return { from: monday, to: addDays(monday, 6) };
  }
  const first = `${today.slice(0, 7)}-01`;
  return { from: first, to: lastDayOfMonth(first) };
}

/** The period before `range`: yesterday, the week before, the month before. */
export function previousRange(period: DashboardPeriod, range: DateRange): DateRange {
  if (period === 'day') {
    const day = addDays(range.from, -1);
    return { from: day, to: day };
  }
  if (period === 'week') return { from: addDays(range.from, -7), to: addDays(range.from, -1) };
  const last = addDays(range.from, -1);
  return { from: `${last.slice(0, 7)}-01`, to: last };
}

/** The instants a range covers in a time zone: from midnight of `from` up to (not including) midnight after `to`. */
export function rangeInstants(
  range: DateRange,
  timeZone: string,
  reference: Date,
): { fromIso: string; toIso: string } {
  const offsetMs = timezoneOffsetMinutes(timeZone, reference) * 60_000;
  const at = (date: string): string =>
    new Date(Date.parse(`${date}T00:00:00Z`) - offsetMs).toISOString();
  return { fromIso: at(range.from), toIso: at(addDays(range.to, 1)) };
}

/** Whole days in a range. */
export function rangeDays(range: DateRange): number {
  return daysBetweenDates(range.from, range.to) + 1;
}

/** Days of the range that are already over (strictly before `today`). */
export function pastDays(range: DateRange, today: string): number {
  if (today <= range.from) return 0;
  return Math.min(rangeDays(range), daysBetweenDates(range.from, today));
}

// ---------------------------------------------------------------------------- scope

export type DashboardScope =
  { kind: 'group' } | { kind: 'entity'; id: string } | { kind: 'branch'; id: string };

/** Where a record belongs: the branch and country that took it in, and the ones that serve it. */
export interface ScopeRef {
  sectorId?: string | null | undefined;
  branchId?: string | null | undefined;
  destinationSectorId?: string | null | undefined;
  servingBranchId?: string | null | undefined;
}

/** A shipment is in a country's view at either end, and in a branch's at the branch that took it in or serves it. */
export function inDashboardScope(scope: DashboardScope, ref: ScopeRef): boolean {
  if (scope.kind === 'group') return true;
  if (scope.kind === 'entity') {
    return ref.sectorId === scope.id || ref.destinationSectorId === scope.id;
  }
  return ref.branchId === scope.id || ref.servingBranchId === scope.id;
}

/** Sales and customers belong to where they were taken in only. */
export function inOriginScope(scope: DashboardScope, ref: ScopeRef): boolean {
  if (scope.kind === 'group') return true;
  return scope.kind === 'entity' ? ref.sectorId === scope.id : ref.branchId === scope.id;
}

export function scopeId(scope: DashboardScope): string {
  return scope.kind === 'group' ? 'group' : scope.id;
}

// ---------------------------------------------------------------------------- speed

const SERVICE_LABELS: Readonly<Record<string, string>> = {
  sea: 'Sea',
  air: 'Air',
  prime: 'Prime',
};

export interface SpeedRow {
  route: string;
  service: 'sea' | 'air' | 'prime';
  region: string;
  cityId: string;
  cityName: string;
  partnerId: string | null;
  partnerName: string | null;
  doorToDoorDays: number;
  onTime: boolean | null;
  lastMileOnTime: boolean | null;
  legs: LegDays;
}

export interface D2dStatOut {
  key: string;
  label: string;
  count: number;
  medianDays: number;
  p90Days: number;
  avgDays: number;
}

export interface OnTimeStatOut {
  key: string;
  label: string;
  total: number;
  onTime: number;
  pct: number | null;
}

export interface RouteLegsOut {
  key: string;
  label: string;
  service: 'sea' | 'air' | 'prime' | 'all';
  count: number;
  avgDays: Record<LegKey, number | null>;
  totalDays: number;
}

export interface SpeedOut {
  delivered: number;
  doorToDoor: {
    count: number;
    medianDays: number | null;
    p90Days: number | null;
    avgDays: number | null;
  };
  byRoute: D2dStatOut[];
  byService: D2dStatOut[];
  byRegion: D2dStatOut[];
  onTime: {
    total: number;
    onTime: number;
    pct: number | null;
    byRoute: OnTimeStatOut[];
    byPartner: OnTimeStatOut[];
    byCity: OnTimeStatOut[];
  };
  legs: RouteLegsOut[];
}

function group<T>(rows: readonly T[], keyOf: (row: T) => string): Map<string, T[]> {
  const out = new Map<string, T[]>();
  for (const row of rows) {
    const key = keyOf(row);
    out.set(key, [...(out.get(key) ?? []), row]);
  }
  return out;
}

const bySizeThenKey =
  <T extends { key: string }>(count: (row: T) => number) =>
  (a: T, b: T): number =>
    count(b) - count(a) || a.key.localeCompare(b.key);

function d2dStats(
  rows: readonly SpeedRow[],
  keyOf: (row: SpeedRow) => string,
  labelOf: (row: SpeedRow) => string,
  cap: number,
): D2dStatOut[] {
  return [...group(rows, keyOf)]
    .map(([key, list]) => {
      const s = summariseDays(list.map((r) => r.doorToDoorDays));
      return {
        key,
        label: labelOf(list[0] as SpeedRow),
        count: s.count,
        medianDays: s.medianDays as number,
        p90Days: s.p90Days as number,
        avgDays: s.avgDays as number,
      };
    })
    .sort(bySizeThenKey((s) => s.count))
    .slice(0, cap);
}

function onTimeStats(
  rows: readonly SpeedRow[],
  keyOf: (row: SpeedRow) => string | null,
  labelOf: (row: SpeedRow) => string,
  flagOf: (row: SpeedRow) => boolean | null,
  cap: number,
): OnTimeStatOut[] {
  const known = rows.filter((r) => keyOf(r) !== null && flagOf(r) !== null);
  return [...group(known, (r) => keyOf(r) as string)]
    .map(([key, list]) => {
      const onTime = list.filter((r) => flagOf(r) === true).length;
      return {
        key,
        label: labelOf(list[0] as SpeedRow),
        total: list.length,
        onTime,
        pct: percentOf(onTime, list.length),
      };
    })
    .sort(bySizeThenKey((s) => s.total))
    .slice(0, cap);
}

/**
 * Door-to-door by route, service and destination region; on-time by route (door to door against
 * the route's target), by partner and by city (the last mile against the city's target, else door
 * to door); and the average leg breakdown per route and service.
 */
export function speedSummary(rows: readonly SpeedRow[]): SpeedOut {
  const all = summariseDays(rows.map((r) => r.doorToDoorDays));
  const flagged = rows.filter((r) => r.onTime !== null);
  const onTimeCount = flagged.filter((r) => r.onTime === true).length;
  const lastMileOrD2d = (r: SpeedRow): boolean | null => r.lastMileOnTime ?? r.onTime;

  const legs: RouteLegsOut[] = [...group(rows, (r) => `${r.route}__${r.service}`)]
    .map(([key, list]) => {
      const first = list[0] as SpeedRow;
      const avgDays = Object.fromEntries(
        LEG_KEYS.map((leg) => {
          const values = list.map((r) => r.legs[leg]).filter((v): v is number => v !== null);
          const mean = meanOf(values);
          return [leg, mean === null ? null : round2(mean)];
        }),
      ) as Record<LegKey, number | null>;
      const totalDays = round2(LEG_KEYS.reduce((s, leg) => s + (avgDays[leg] ?? 0), 0));
      return {
        key,
        label: `${first.route} · ${SERVICE_LABELS[first.service] ?? first.service}`,
        service: first.service,
        count: list.length,
        avgDays,
        totalDays,
      };
    })
    .sort(bySizeThenKey((s) => s.count))
    .slice(0, 60);

  return {
    delivered: rows.length,
    doorToDoor: {
      count: all.count,
      medianDays: all.medianDays,
      p90Days: all.p90Days,
      avgDays: all.avgDays,
    },
    byRoute: d2dStats(
      rows,
      (r) => r.route,
      (r) => r.route,
      60,
    ),
    byService: d2dStats(
      rows,
      (r) => r.service,
      (r) => SERVICE_LABELS[r.service] ?? r.service,
      5,
    ),
    byRegion: d2dStats(
      rows,
      (r) => r.region,
      (r) => r.region,
      60,
    ),
    onTime: {
      total: flagged.length,
      onTime: onTimeCount,
      pct: percentOf(onTimeCount, flagged.length),
      byRoute: onTimeStats(
        rows,
        (r) => r.route,
        (r) => r.route,
        (r) => r.onTime,
        60,
      ),
      byPartner: onTimeStats(
        rows,
        (r) => r.partnerId,
        (r) => r.partnerName ?? (r.partnerId as string),
        lastMileOrD2d,
        60,
      ),
      byCity: onTimeStats(
        rows,
        (r) => r.cityId,
        (r) => r.cityName,
        lastMileOrD2d,
        100,
      ),
    },
    legs,
  };
}

// ----------------------------------------------------------------------- box stages

export const BOX_STAGES = [
  'waiting_payment',
  'at_origin',
  'on_trip',
  'in_container',
  'at_sea',
  'at_port',
  'at_destination_hub',
  'with_partner',
  'out_for_delivery',
] as const;
export type BoxStage = (typeof BOX_STAGES)[number];

export interface StageUnit {
  status: BoxStatus;
  /** The shipment is paid in full. */
  paid: boolean;
  /** The branch that last received it is in the destination country. */
  atDestination: boolean;
  /** Time since its last event, milliseconds. */
  ageMs: number;
}

/** The funnel stage of a box, or undefined once it is delivered, returned, cancelled or lost. */
export function boxStage(
  unit: Pick<StageUnit, 'status' | 'paid' | 'atDestination'>,
): BoxStage | undefined {
  switch (unit.status) {
    case 'at_branch':
      if (unit.atDestination) return 'at_destination_hub';
      return unit.paid ? 'at_origin' : 'waiting_payment';
    case 'on_trip':
      return 'on_trip';
    case 'in_container':
      return 'in_container';
    case 'exported':
      return 'at_sea';
    case 'at_port':
      return 'at_port';
    case 'with_partner':
      return 'with_partner';
    case 'out_for_delivery':
      return 'out_for_delivery';
    default:
      return undefined;
  }
}

export interface StageStatOut {
  stage: BoxStage;
  count: number;
  avgAgeDays: number;
  oldestAgeDays: number;
}

/** Boxes by stage with the average and oldest time in the stage. Every stage is listed, in journey order. */
export function boxStageStats(units: readonly StageUnit[]): {
  total: number;
  stages: StageStatOut[];
} {
  const ages = new Map<BoxStage, number[]>(BOX_STAGES.map((s) => [s, []]));
  for (const unit of units) {
    const stage = boxStage(unit);
    if (stage) (ages.get(stage) as number[]).push(Math.max(0, unit.ageMs));
  }
  const stages = BOX_STAGES.map((stage): StageStatOut => {
    const list = ages.get(stage) as number[];
    const mean = meanOf(list);
    return {
      stage,
      count: list.length,
      avgAgeDays: mean === null ? 0 : msToDays(mean),
      oldestAgeDays: list.length === 0 ? 0 : msToDays(Math.max(...list)),
    };
  });
  return { total: stages.reduce((s, x) => s + x.count, 0), stages };
}

// ---------------------------------------------------------------------------- money

export type AccountBucket = 'revenue' | 'otherIncome' | 'cargoCost' | 'runningCost';

/** Which line of the result an account belongs to. Accounts that are not income or cost give undefined. */
export function accountBucket(accountCode: string): AccountBucket | undefined {
  const account = getAccount(accountCode);
  switch (account?.type) {
    case 'revenue':
      return accountCode === '4900' ? 'otherIncome' : 'revenue';
    case 'contra_revenue':
      return 'revenue';
    case 'cogs':
      return 'cargoCost';
    case 'opex':
    case 'other':
      return 'runningCost';
    default:
      return undefined;
  }
}

export interface LineFact {
  accountCode: string;
  debitMinor: number;
  creditMinor: number;
}

export interface RawTotals {
  revenue: number;
  otherIncome: number;
  cargoCosts: number;
  runningCosts: number;
}

export const emptyTotals = (): RawTotals => ({
  revenue: 0,
  otherIncome: 0,
  cargoCosts: 0,
  runningCosts: 0,
});

/** Adds one journal line: income by credits less debits, costs by debits less credits. */
export function addLine(totals: RawTotals, line: LineFact): void {
  const bucket = accountBucket(line.accountCode);
  const net = line.creditMinor - line.debitMinor;
  if (bucket === 'revenue') totals.revenue += net;
  else if (bucket === 'otherIncome') totals.otherIncome += net;
  else if (bucket === 'cargoCost') totals.cargoCosts -= net;
  else if (bucket === 'runningCost') totals.runningCosts -= net;
}

export interface DerivedTotals extends RawTotals {
  /** Revenue less cargo costs. */
  cargoMargin: number;
  /** Cargo margin plus other income less running costs. */
  result: number;
}

export function deriveTotals(t: RawTotals): DerivedTotals {
  const cargoMargin = t.revenue - t.cargoCosts;
  return { ...t, cargoMargin, result: cargoMargin + t.otherIncome - t.runningCosts };
}

export function sumTotals(list: readonly RawTotals[]): RawTotals {
  return list.reduce(
    (s, t) => ({
      revenue: s.revenue + t.revenue,
      otherIncome: s.otherIncome + t.otherIncome,
      cargoCosts: s.cargoCosts + t.cargoCosts,
      runningCosts: s.runningCosts + t.runningCosts,
    }),
    emptyTotals(),
  );
}

/** Whole minor units, half up (also for negatives, away from zero). */
export function roundDiv(numerator: number, denominator: number): number {
  const q = numerator / denominator;
  return q < 0 ? -Math.round(-q) : Math.round(q);
}

/** The mean per box of an amount; null without boxes. */
export function perBoxMinor(amountMinor: number, boxes: number): number | null {
  return boxes <= 0 ? null : roundDiv(amountMinor, boxes);
}

export interface ContainerFacts {
  containerId: string;
  containerNo: string;
  route: string;
  status: string;
  boxes: number;
  plannedBoxes: number | null;
  /** Revenue of the boxes in AED; null when a shipment's currency has no rate. */
  revenueAedMinor: number | null;
  /** Costs accrued for the container, port to port (export, customs, pull-out, its bills), in AED; null when a cost had no rate. */
  costsAedMinor: number | null;
  /**
   * The last mile of its boxes, in AED: the forwarding accrued on the handovers of its boxes and,
   * for boxes not handed over, the cost model's delivery and forwarding share. Null when a cost had
   * no rate; left out (0) by callers that do not know it.
   */
  lastMileAedMinor?: number | null;
  /** What the cost model says one box costs on this container, port to port, in AED. */
  estimatePerBoxAedMinor: number | null;
  devanned: boolean;
}

export interface ContainerEconomicsOut {
  containerId: string;
  containerNo: string;
  route: string;
  status: string;
  boxes: number;
  plannedBoxes: number | null;
  fillPct: number | null;
  revenueAedMinor: number | null;
  /** The whole journey: port to port plus the last mile of its boxes. */
  costsAedMinor: number | null;
  /** Revenue less the whole journey: the main figure (15 §15.1). */
  marginAedMinor: number | null;
  marginPerBoxAedMinor: number | null;
  costPerBoxAedMinor: number | null;
  /** Port to port only (what the container's own journals and bills carry). */
  portCostsAedMinor: number | null;
  portMarginAedMinor: number | null;
  portCostPerBoxAedMinor: number | null;
  /** Forwarding and delivery of its boxes. */
  lastMileAedMinor: number | null;
  estimatePerBoxAedMinor: number | null;
  /** Port-to-port cost per box against the estimate (the cost model's stages reached so far). */
  variancePct: number | null;
  /** Boxes at which the revenue per box covers the whole journey. */
  breakEvenBoxes: number | null;
  costsComplete: boolean;
}

/**
 * One container's result over the whole journey (15 §15.1): revenue of its boxes less the costs
 * accrued for it port to port and the last mile of its boxes (forwarding and delivery). The
 * port-to-port figures are kept beside it. Break-even is the number of boxes at which the revenue
 * per box carried so far would cover the whole journey; the variance compares the port-to-port
 * cost per box with the cost model's estimate of the stages reached.
 */
export function containerEconomics(f: ContainerFacts): ContainerEconomicsOut {
  const lastMile = f.lastMileAedMinor === undefined ? 0 : f.lastMileAedMinor;
  const port = f.costsAedMinor;
  const full = port === null || lastMile === null ? null : port + lastMile;
  const marginOf = (cost: number | null) =>
    f.revenueAedMinor === null || cost === null ? null : f.revenueAedMinor - cost;
  const margin = marginOf(full);
  const costPerBox = full === null ? null : perBoxMinor(full, f.boxes);
  const portPerBox = port === null ? null : perBoxMinor(port, f.boxes);
  const revenuePerBox = f.revenueAedMinor === null ? null : perBoxMinor(f.revenueAedMinor, f.boxes);
  const variancePct =
    portPerBox === null || f.estimatePerBoxAedMinor === null || f.estimatePerBoxAedMinor <= 0
      ? null
      : round1(((portPerBox - f.estimatePerBoxAedMinor) / f.estimatePerBoxAedMinor) * 100);
  return {
    containerId: f.containerId,
    containerNo: f.containerNo,
    route: f.route,
    status: f.status,
    boxes: f.boxes,
    plannedBoxes: f.plannedBoxes,
    fillPct:
      f.plannedBoxes === null || f.plannedBoxes <= 0
        ? null
        : Math.round((f.boxes / f.plannedBoxes) * 100),
    revenueAedMinor: f.revenueAedMinor,
    costsAedMinor: full,
    marginAedMinor: margin,
    marginPerBoxAedMinor: margin === null ? null : perBoxMinor(margin, f.boxes),
    costPerBoxAedMinor: costPerBox,
    portCostsAedMinor: port,
    portMarginAedMinor: marginOf(port),
    portCostPerBoxAedMinor: portPerBox,
    lastMileAedMinor: lastMile,
    estimatePerBoxAedMinor: f.estimatePerBoxAedMinor,
    variancePct,
    breakEvenBoxes:
      revenuePerBox === null || revenuePerBox <= 0 || full === null
        ? null
        : Math.ceil(full / revenuePerBox),
    costsComplete: f.devanned,
  };
}

/** Actual cost per box against the cost model's estimate, over the containers that have both. */
export function costAccuracy(list: readonly ContainerEconomicsOut[]): {
  containers: number;
  actualPerBoxAedMinor: number | null;
  estimatePerBoxAedMinor: number | null;
  variancePct: number | null;
} {
  // The estimate is port to port, so it is measured against the port-to-port cost.
  const both = list.filter(
    (c) => c.portCostPerBoxAedMinor !== null && c.estimatePerBoxAedMinor !== null,
  );
  const actual = meanOf(both.map((c) => c.portCostPerBoxAedMinor as number));
  const estimate = meanOf(both.map((c) => c.estimatePerBoxAedMinor as number));
  return {
    containers: both.length,
    actualPerBoxAedMinor: actual === null ? null : Math.round(actual),
    estimatePerBoxAedMinor: estimate === null ? null : Math.round(estimate),
    variancePct:
      actual === null || estimate === null || estimate <= 0
        ? null
        : round1(((actual - estimate) / estimate) * 100),
  };
}

/** Average break-even boxes and average fill over the containers that have them. */
export function breakEvenSummary(list: readonly ContainerEconomicsOut[]): {
  avgBoxes: number | null;
  avgFillPct: number | null;
} {
  const boxes = meanOf(list.flatMap((c) => (c.breakEvenBoxes === null ? [] : [c.breakEvenBoxes])));
  const fill = meanOf(list.flatMap((c) => (c.fillPct === null ? [] : [c.fillPct])));
  return {
    avgBoxes: boxes === null ? null : Math.round(boxes),
    avgFillPct: fill === null ? null : round1(fill),
  };
}

/** Reserves held as a whole percent of what is required; null when nothing is required. */
export function reservesCoveragePct(heldMinor: number, requiredMinor: number): number | null {
  return requiredMinor <= 0 ? null : Math.floor((heldMinor * 100) / requiredMinor);
}

// ------------------------------------------------------------------------- customers

export interface ShipperFact {
  customerId: string;
  /** `stats.firstShipmentAt` of the customer, null when unknown. */
  firstShipmentAt: string | null;
}

/** Customers who shipped in the period and how many had shipped before it started. */
export function repeatFigures(
  shippers: readonly ShipperFact[],
  periodStartIso: string,
): { customers: number; repeat: number; pct: number | null } {
  const seen = new Set<string>();
  let repeat = 0;
  for (const s of shippers) {
    if (seen.has(s.customerId)) continue;
    seen.add(s.customerId);
    if (s.firstShipmentAt !== null && s.firstShipmentAt < periodStartIso) repeat++;
  }
  return { customers: seen.size, repeat, pct: percentOf(repeat, seen.size) };
}

/** New customers by how they found us, most first; a customer with no source counts as "unknown". */
export function acquisitionCounts(
  sources: readonly (string | undefined)[],
): { source: string; count: number }[] {
  const counts = new Map<string, number>();
  for (const s of sources) counts.set(s || 'unknown', (counts.get(s || 'unknown') ?? 0) + 1);
  return [...counts]
    .map(([source, count]) => ({ source, count }))
    .sort((a, b) => b.count - a.count || a.source.localeCompare(b.source))
    .slice(0, 10);
}

/** Spend divided by the customers it brought, per customer; null without either. */
export function costPerNewCustomer(spendMinor: number | null, newCustomers: number): number | null {
  return spendMinor === null || newCustomers <= 0 ? null : roundDiv(spendMinor, newCustomers);
}

export interface SatisfactionOut {
  ratings: number;
  csat: number | null;
  nps: number | null;
  distribution: [number, number, number, number, number];
}

/** Average stars and the net score (4-5 stars less 1-3 stars, as `feedbackScores`) from the count of each rating. */
export function satisfaction(
  distribution: readonly [number, number, number, number, number],
): SatisfactionOut {
  const ratings = distribution.reduce((s, n) => s + n, 0);
  const stars = distribution.reduce((s, n, i) => s + n * (i + 1), 0);
  const happy = distribution[3] + distribution[4];
  const unhappy = distribution[0] + distribution[1] + distribution[2];
  return {
    ratings,
    csat: ratings === 0 ? null : round2(stars / ratings),
    nps: ratings === 0 ? null : round1(((happy - unhappy) / ratings) * 100),
    distribution: [...distribution] as SatisfactionOut['distribution'],
  };
}

export interface CustomerRevenueFact {
  customerId: string;
  name: string;
  boxes: number;
  /** Revenue of one shipment in the scope's currency and in AED. */
  revenueMinor: number;
  revenueAedMinor: number | null;
}

export interface TopCustomerOut {
  customerId: string;
  name: string;
  shipments: number;
  boxes: number;
  revenueMinor: number;
  revenueAedMinor: number | null;
}

/** The customers with the most revenue in the period (ties: more shipments), at most ten. */
export function topCustomers(facts: readonly CustomerRevenueFact[], limit = 10): TopCustomerOut[] {
  const byCustomer = new Map<string, TopCustomerOut>();
  for (const f of facts) {
    const row = byCustomer.get(f.customerId) ?? {
      customerId: f.customerId,
      name: f.name,
      shipments: 0,
      boxes: 0,
      revenueMinor: 0,
      revenueAedMinor: 0 as number | null,
    };
    row.shipments++;
    row.boxes += f.boxes;
    row.revenueMinor += f.revenueMinor;
    row.revenueAedMinor =
      row.revenueAedMinor === null || f.revenueAedMinor === null
        ? null
        : row.revenueAedMinor + f.revenueAedMinor;
    byCustomer.set(f.customerId, row);
  }
  return [...byCustomer.values()]
    .sort(
      (a, b) =>
        b.revenueMinor - a.revenueMinor ||
        b.shipments - a.shipments ||
        a.customerId.localeCompare(b.customerId),
    )
    .slice(0, limit);
}

// ----------------------------------------------------------------------------- health

export interface OpenExceptionFact {
  type: string;
  /** When it was raised, epoch milliseconds. */
  raisedAtMs: number;
}

export interface ExceptionHealthOut {
  open: number;
  byType: { type: string; count: number; avgAgeHours: number; oldestAgeHours: number }[];
  byAge: { under4h: number; h4to24: number; d1to3: number; over3d: number };
}

/** Open exceptions by type and by age (under 4 hours, 4-24 hours, 1-3 days, older). */
export function exceptionHealth(
  list: readonly OpenExceptionFact[],
  nowMs: number,
): ExceptionHealthOut {
  const byAge = { under4h: 0, h4to24: 0, d1to3: 0, over3d: 0 };
  const types = new Map<string, number[]>();
  for (const e of list) {
    const age = Math.max(0, nowMs - e.raisedAtMs);
    types.set(e.type, [...(types.get(e.type) ?? []), age]);
    if (age < 4 * HOUR_MS) byAge.under4h++;
    else if (age < DAY_MS) byAge.h4to24++;
    else if (age < 3 * DAY_MS) byAge.d1to3++;
    else byAge.over3d++;
  }
  return {
    open: list.length,
    byType: [...types]
      .map(([type, ages]) => ({
        type,
        count: ages.length,
        avgAgeHours: round2((meanOf(ages) as number) / HOUR_MS),
        oldestAgeHours: round2(Math.max(...ages) / HOUR_MS),
      }))
      .sort((a, b) => b.count - a.count || a.type.localeCompare(b.type))
      .slice(0, 20),
    byAge,
  };
}

/**
 * A pickup is on time when it was completed by the end of its window: the window's date and its
 * `to` time (HH:MM) in the branch's time zone. A window with no readable end runs to midnight.
 */
export function pickupOnTime(
  window: { date: string; to?: string | undefined },
  completedAtIso: string,
  timeZone: string,
): boolean {
  const done = Date.parse(completedAtIso);
  if (!Number.isFinite(done)) return false;
  const hhmm = /^([01]\d|2[0-3]):([0-5]\d)$/.exec(window.to ?? '');
  const end = hhmm
    ? Date.parse(`${window.date}T${hhmm[1]}:${hhmm[2]}:00Z`)
    : Date.parse(`${addDays(window.date, 1)}T00:00:00Z`);
  const offsetMs = timezoneOffsetMinutes(timeZone, new Date(end)) * 60_000;
  return done <= end - offsetMs;
}

// ----------------------------------------------------------------------- comparison

export interface ComparisonOut {
  current: number | null;
  previous: number | null;
  change: number | null;
  changePct: number | null;
}

/** This period against the one before: the difference, and the difference as a percentage of the earlier figure. */
export function compareFigure(current: number | null, previous: number | null): ComparisonOut {
  if (current === null || previous === null) {
    return { current, previous, change: null, changePct: null };
  }
  const change = round2(current - previous);
  return {
    current,
    previous,
    change,
    changePct: previous === 0 ? null : round1((change / Math.abs(previous)) * 100),
  };
}
